import { exists } from 'helpers/exists'
import { useIsomorphicLayoutEffect } from 'helpers/hooks/useIsomorphicLayoutEffect'
import { useTranslation } from 'helpers/hooks/useTranslation'
import React, { FC, ReactNode, useRef, useState } from 'react'
import styled, { css } from 'styled-components'

const LABEL_COUNT_BEFORE_CUT_OFF = 5

const commaJoinLabels = (labels: ReactNode[]) => {
  return labels.map((label, index, list) => (
    <React.Fragment key={index}>
      {label}
      {index < list.length - 1 ? `, ` : ``}
    </React.Fragment>
  ))
}

const EllipsisWrap = styled.div<{ overflowing: boolean }>`
  max-width: 100%;
  white-space: nowrap;
  ${props =>
    props.overflowing &&
    css`
      overflow: hidden;
      text-overflow: ellipsis;
    `};

  & * {
    display: inline !important;
  }
`

const MultiSelectLabel: FC<{
  labels: ReactNode[]
  placeholder?: ReactNode
}> = ({ labels, placeholder = '...' }) => {
  const { t } = useTranslation()
  const [isOverflowing, setOverflowing] = useState(false)

  const ref = useRef<HTMLDivElement>(null)
  const lastLabelsLength = useRef(0)

  useIsomorphicLayoutEffect(() => {
    if (isOverflowing && lastLabelsLength.current >= labels.length) {
      lastLabelsLength.current = labels.length
      return
    }

    const measuredOverflow =
      exists(ref.current) && ref.current.offsetWidth < ref.current.scrollWidth

    if (measuredOverflow !== isOverflowing) {
      setOverflowing(measuredOverflow)
    }
  }, [ref.current, isOverflowing, labels])

  let content: ReactNode
  if (isOverflowing && labels.length > LABEL_COUNT_BEFORE_CUT_OFF) {
    content = commaJoinLabels(labels.slice(0, LABEL_COUNT_BEFORE_CUT_OFF))

    content = (
      <>
        {content}, {labels.length - LABEL_COUNT_BEFORE_CUT_OFF} {t('weitere')}
      </>
    )
  } else if (labels.length > 0) {
    content = commaJoinLabels(labels)
  } else {
    content = <>{placeholder}</>
  }

  return (
    <EllipsisWrap
      data-testid="DropdownMultiSelectLabel"
      overflowing={isOverflowing}
      ref={ref}
    >
      {content}
    </EllipsisWrap>
  )
}

export default MultiSelectLabel
